import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Clock, Timer, Settings2 } from 'lucide-react';
import { attendanceRulesAPI, workspaceAdminAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import WeekOffPicker from '../components/common/WeekOffPicker';
import { getErrorMessage } from '../lib/utils';

const initialForm = {
  name: '',
  start_time: '09:00:00',
  end_time: '18:00:00',
  grace_period_minutes: 5,
  early_exit_tolerance_minutes: 5,
  half_day_min_minutes: 240,
  full_day_min_minutes: 480,
  break_duration_minutes: 60,
  is_default: false,
  auto_half_day_enabled: true,
  fractional_leave_allowed: true,
  minimum_leave_unit: 0.5,
  // Empty follows the tenant's week off; anything here overrides it for
  // everybody on this shift, including the payroll divisor.
  week_off_days: [],
};

const parseInteger = (value, fallback = 0) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseFloatValue = (value, fallback = 0.5) => {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const AdminAttendanceShiftCreatePage = ({
  routeBasePath = '/admin/rules-shifts',
  scope = 'tenant',
}) => {
  const navigate = useNavigate();
  // Under /superadmin this creates a template entry, not a shift inside any
  // workspace — the tenant endpoint is proxy-only and would refuse.
  const isGlobalScope = scope === 'global';
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error('Shift name is required');
      return;
    }

    try {
      setSubmitting(true);
      const createShift = isGlobalScope
        ? workspaceAdminAPI.createGlobalShift
        : attendanceRulesAPI.createShift;
      await createShift({
        ...form,
        name: form.name.trim(),
        grace_period_minutes: parseInteger(form.grace_period_minutes, 0),
        early_exit_tolerance_minutes: parseInteger(form.early_exit_tolerance_minutes, 0),
        half_day_min_minutes: parseInteger(form.half_day_min_minutes, 240),
        full_day_min_minutes: parseInteger(form.full_day_min_minutes, 480),
        break_duration_minutes: parseInteger(form.break_duration_minutes, 60),
        minimum_leave_unit: parseFloatValue(form.minimum_leave_unit, 0.5),
      });
      navigate(routeBasePath, {
        state: {
          shiftCreatedMessage: 'Shift created successfully',
          shiftCreatedAt: Date.now(),
        },
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create shift'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Create Work Shift</h2>
          <p className="text-sm text-muted-foreground">Configure timings, tolerances, and attendance thresholds.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(routeBasePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button type="submit" disabled={submitting}>
            <Save className="h-4 w-4 mr-2" />
            Create Shift
          </Button>
        </div>
      </div>

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
              <Clock className="h-4.5 w-4.5" />
            </span>
            <div>
              <CardTitle className="text-foreground">Shift Details & Timings</CardTitle>
              <p className="text-sm text-muted-foreground">Name the shift and set its working window.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2 sm:col-span-2 lg:col-span-3">
            <Label htmlFor="shift-name">Shift Name</Label>
            <Input
              id="shift-name"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="General Shift"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-start-time">Start Time</Label>
            <Input
              id="shift-start-time"
              type="time"
              step="1"
              value={form.start_time}
              onChange={(event) => setForm((current) => ({ ...current, start_time: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-end-time">End Time</Label>
            <Input
              id="shift-end-time"
              type="time"
              step="1"
              value={form.end_time}
              onChange={(event) => setForm((current) => ({ ...current, end_time: event.target.value }))}
            />
          </div>

          <div className="space-y-2 sm:col-span-2 lg:col-span-3">
            <Label>Week Off</Label>
            <WeekOffPicker
              value={form.week_off_days}
              onChange={(days) => setForm((current) => ({ ...current, week_off_days: days }))}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to follow the workspace week off. Payroll divides pay by the
              days this shift actually works, so a six-day shift and a five-day one are
              costed differently.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-break-duration">Break Duration (mins)</Label>
            <Input
              id="shift-break-duration"
              type="number"
              min="0"
              value={form.break_duration_minutes}
              onChange={(event) => setForm((current) => ({ ...current, break_duration_minutes: event.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
              <Timer className="h-4.5 w-4.5" />
            </span>
            <div>
              <CardTitle className="text-foreground">Tolerances & Thresholds</CardTitle>
              <p className="text-sm text-muted-foreground">Grace windows and day-length rules used for attendance.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="shift-grace-period">Grace Period (mins)</Label>
            <Input
              id="shift-grace-period"
              type="number"
              min="0"
              value={form.grace_period_minutes}
              onChange={(event) => setForm((current) => ({ ...current, grace_period_minutes: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-early-exit">Early Exit Tolerance (mins)</Label>
            <Input
              id="shift-early-exit"
              type="number"
              min="0"
              value={form.early_exit_tolerance_minutes}
              onChange={(event) => setForm((current) => ({ ...current, early_exit_tolerance_minutes: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-minimum-leave-unit">Minimum Leave Unit</Label>
            <Input
              id="shift-minimum-leave-unit"
              type="number"
              min="0.25"
              step="0.25"
              value={form.minimum_leave_unit}
              onChange={(event) => setForm((current) => ({ ...current, minimum_leave_unit: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-half-day">Half Day Min (mins)</Label>
            <Input
              id="shift-half-day"
              type="number"
              min="0"
              value={form.half_day_min_minutes}
              onChange={(event) => setForm((current) => ({ ...current, half_day_min_minutes: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shift-full-day">Full Day Min (mins)</Label>
            <Input
              id="shift-full-day"
              type="number"
              min="0"
              value={form.full_day_min_minutes}
              onChange={(event) => setForm((current) => ({ ...current, full_day_min_minutes: event.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
              <Settings2 className="h-4.5 w-4.5" />
            </span>
            <div>
              <CardTitle className="text-foreground">Shift Behavior</CardTitle>
              <p className="text-sm text-muted-foreground">Automation and leave handling for this shift.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(event) => setForm((current) => ({ ...current, is_default: event.target.checked }))}
              className="h-4 w-4"
            />
            Set as default shift
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={form.auto_half_day_enabled}
              onChange={(event) => setForm((current) => ({ ...current, auto_half_day_enabled: event.target.checked }))}
              className="h-4 w-4"
            />
            Enable auto half day
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={form.fractional_leave_allowed}
              onChange={(event) => setForm((current) => ({ ...current, fractional_leave_allowed: event.target.checked }))}
              className="h-4 w-4"
            />
            Allow fractional leave
          </label>
        </CardContent>
      </Card>
    </form>
  );
};

export default AdminAttendanceShiftCreatePage;
